import { Button } from '@mantine/core'
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import i18next from 'i18next'
import { TbReload } from 'react-icons/tb'

import { getAllBillingPeriodsOptions } from '@/api/main/@tanstack/react-query.gen'
import { pickDefaultBillingPeriod } from '@/components/Billing/billingPeriods'
import ErrorState from '@/components/Common/ErrorState'
import PageShell from '@/components/Common/PageShell'
import { requirePermission } from '@/utils/auth'
import { getTimezone } from '@/utils/datetime'

export const Route = createFileRoute('/billing')({
  beforeLoad: async ({ context, location }) => {
    requirePermission('billing')

    if (location.pathname === '/billing' || location.pathname === '/billing/') {
      const periods = await context.queryClient.ensureQueryData(getAllBillingPeriodsOptions())
      const period = pickDefaultBillingPeriod(periods, new Date(), getTimezone())

      // Without any period, fall through to the index route's empty state.
      if (period) {
        throw redirect({
          to: '/billing/$billingPeriodId',
          params: { billingPeriodId: String(period.id) },
        })
      }
    }
  },
  // Without this, a failing period list (fetched above for the redirect) falls through to the
  // router's default error component, which renders a blank page.
  errorComponent: BillingErrorPage,
})

function BillingErrorPage({ error }: { error: Error }) {
  const router = useRouter()
  return (
    <PageShell title={i18next.t('title', { ns: 'billing' })} fullWidth>
      <ErrorState
        title={i18next.t('failedToLoad', { ns: 'billing' })}
        message={error.message || i18next.t('unableToLoadPeriods', { ns: 'billing' })}
        customAction={
          <Button
            mt="md"
            leftSection={<TbReload size={16} />}
            variant="light"
            color="red"
            size="sm"
            onClick={() => router.invalidate()}
          >
            {i18next.t('retry', { ns: 'common' })}
          </Button>
        }
      />
    </PageShell>
  )
}
